
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { loginThunk, logoutThunk } from "./index";
import { AuthState, User } from "../../types";
import { setAuthToken, setUserData, clearAuthCookies, getAuthToken, getUserData } from "../../utils/cookieUtils";

const getInitialState = (): AuthState => {
  const token = getAuthToken();
  const user = getUserData();
  
  return {
    user: user || null,
    token: token || null,
    loading: false,
    error: null,
  };
};

const authSlice = createSlice({
  name: "auth",
  initialState: getInitialState(),
  reducers: {
    logout: (state: AuthState) => {
      state.user = null;
      state.token = null;
      state.error = null;
      clearAuthCookies();
    },
    clearError: (state: AuthState) => {
      state.error = null;
    },
    initializeAuth: (state: AuthState) => {
      const token = getAuthToken();
      const user = getUserData();
      if (token && user) {
        state.user = user;
        state.token = token;
      }
    },
  },
  extraReducers: (builder: any) => {
    builder
      .addCase(loginThunk.pending, (state: AuthState) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginThunk.fulfilled, (state: AuthState, action: PayloadAction<{ result: User; token: string }>) => {
        state.loading = false;
        state.user = action.payload.result;
        state.token = action.payload.token;
        state.error = null;
        setAuthToken(action.payload.token);
        setUserData(action.payload.result);
      })
      .addCase(loginThunk.rejected, (state: AuthState, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || "Login failed. Please try again.";
        state.user = null;
        state.token = null;
      })
      .addCase(logoutThunk.pending, (state: AuthState) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logoutThunk.fulfilled, (state: AuthState) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.error = null;
        clearAuthCookies();
      })
      .addCase(logoutThunk.rejected, (state: AuthState, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.error = action.payload || "Logout failed";
        clearAuthCookies();
      });
  },
});

export const { logout, clearError, initializeAuth } = authSlice.actions;
export default authSlice.reducer;
